import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
    ArrowLeft, ArrowRight, Check, Loader2, Target, TrendingUp, Zap, AlertTriangle,
    Heart, Activity, Settings, Sparkles, Mail, Building2, User as UserIcon, Hash,
    Lock,
} from 'lucide-react';
import { Footer } from './landing/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Target, TrendingUp, Zap, AlertTriangle, Heart, Activity, Settings };

const LEVEL_STYLES = {
    expert: { color: '#10B981', bg: 'from-emerald-500/15 to-emerald-500/5', border: 'border-emerald-500/30' },
    structured: { color: '#3B82F6', bg: 'from-blue-500/15 to-blue-500/5', border: 'border-blue-500/30' },
    developing: { color: '#F59E0B', bg: 'from-amber-500/15 to-amber-500/5', border: 'border-amber-500/30' },
    instinct: { color: '#EF4444', bg: 'from-red-500/15 to-red-500/5', border: 'border-red-500/30' },
};

export default function DiagnosticPage() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0); // 0 = identity, 1..N = questions, N+1 = sending, N+2 = result
    const [identity, setIdentity] = useState({ first_name: '', email: '', agency_name: '', nb_agents: '' });
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({}); // {question_id: option_value}
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [consent, setConsent] = useState(true);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        document.documentElement.classList.add('dark');
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', '#0a0a1a');
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        axios.get(`${API}/contact/diagnostic/questions`)
            .then((r) => setQuestions(r.data.questions || []))
            .catch(() => setQuestions([]));
    }, []);

    const totalSteps = 1 + (questions.length || 0); // identity + 7 questions
    const currentProgress = useMemo(() => {
        if (step === 0) return 0;
        if (result) return 100;
        return Math.round((step / totalSteps) * 100);
    }, [step, totalSteps, result]);

    const validateIdentity = () => {
        const e = {};
        if (!identity.first_name.trim()) e.first_name = 'Votre prénom est requis';
        if (!identity.email.trim() || !/\S+@\S+\.\S+/.test(identity.email)) e.email = 'Email valide requis';
        if (!identity.agency_name.trim()) e.agency_name = "Le nom de l'agence est requis";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleNextFromIdentity = () => {
        if (validateIdentity()) setStep(1);
    };

    const handleAnswer = (questionId, optionValue) => {
        if (submitting) return; // Guard against double-clicks during submit
        setAnswers((a) => ({ ...a, [questionId]: optionValue }));
        // auto-advance after a short delay
        setTimeout(() => {
            if (step < totalSteps - 1) setStep((s) => s + 1);
            else handleSubmit({ ...answers, [questionId]: optionValue });
        }, 300);
    };

    const handleBack = () => {
        if (step > 0) setStep((s) => s - 1);
    };

    const handleSubmit = async (finalAnswers) => {
        setSubmitting(true);
        try {
            const payload = {
                first_name: identity.first_name.trim(),
                email: identity.email.trim().toLowerCase(),
                agency_name: identity.agency_name.trim(),
                nb_agents: identity.nb_agents ? parseInt(identity.nb_agents, 10) : null,
                consent_marketing: consent,
                answers: Object.entries(finalAnswers).map(([qid, val]) => ({ question_id: qid, option_value: val })),
            };
            const { data } = await axios.post(`${API}/contact/diagnostic`, payload);
            setResult(data);
            window.scrollTo(0, 0);
        } catch (e) {
            setErrors({ submit: 'Une erreur est survenue. Réessayez dans un instant.' });
        } finally {
            setSubmitting(false);
        }
    };

    const currentQuestion = step > 0 && step <= questions.length ? questions[step - 1] : null;

    return (
        <div className="min-h-screen bg-[#0a0a1a] flex flex-col" data-testid="diagnostic-page">
            {/* Minimal header */}
            <header className="border-b border-white/5 backdrop-blur-md bg-[#0a0a1a]/80 sticky top-0 z-20">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
                    <Link to="/agences" className="flex items-center gap-2 text-white/80 hover:text-white" data-testid="diag-back-home">
                        <span className="w-7 h-7 rounded-md bg-white flex items-center justify-center">
                            <span className="text-[#681793] font-black text-sm">H</span>
                        </span>
                        <span className="font-bold text-white">Homly</span>
                        <span className="text-white/40 text-xs ml-1 hidden sm:inline">· Diagnostic Pilotage</span>
                    </Link>
                    {!result && (
                        <div className="flex items-center gap-3">
                            <div className="hidden sm:block text-xs text-white/40 font-semibold">{currentProgress}%</div>
                            <div className="w-24 sm:w-40 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-gradient-to-r from-[#681793] to-[#FF914D]"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${currentProgress}%` }}
                                    transition={{ duration: 0.4, ease: 'easeOut' }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </header>

            <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-8 sm:py-12">
                <div className="w-full max-w-2xl">
                    <AnimatePresence mode="wait">
                        {/* ─── RESULT ─── */}
                        {result ? (
                            <ResultView key="result" result={result} identity={identity} navigate={navigate} />
                        ) : submitting ? (
                            <motion.div
                                key="submitting"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-20"
                                data-testid="diag-submitting"
                            >
                                <Loader2 className="w-12 h-12 text-[#FF914D] animate-spin mx-auto mb-6" />
                                <h2 className="text-2xl font-bold text-white mb-2">Calcul de votre score…</h2>
                                <p className="text-white/50">Analyse de vos 7 réponses en cours.</p>
                            </motion.div>
                        ) : step === 0 ? (
                            /* ─── STEP 0 : Identity ─── */
                            <motion.div
                                key="identity"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                data-testid="diag-step-identity"
                            >
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#681793]/20 to-[#FF914D]/20 border border-[#FF914D]/30 mb-5">
                                    <Sparkles className="w-3 h-3 text-[#FF914D]" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF914D]">Diagnostic gratuit · 2 minutes</span>
                                </span>
                                <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-3">
                                    Évaluez le pilotage de
                                    <span className="block bg-gradient-to-r from-[#681793] via-[#9b4dca] to-[#FF914D] bg-clip-text text-transparent">
                                        votre agence en 2 minutes.
                                    </span>
                                </h1>
                                <p className="text-white/60 text-sm sm:text-base mb-6 leading-relaxed">
                                    7 questions courtes. Vous recevez votre <strong className="text-white">Score Pilotage / 100</strong>, vos <strong className="text-white">3 angles morts</strong> et un rapport personnalisé par email.
                                </p>

                                <div className="space-y-4 bg-white/[0.03] border border-white/10 rounded-2xl p-5 sm:p-6">
                                    <Field
                                        icon={UserIcon}
                                        label="Votre prénom"
                                        placeholder="Jean"
                                        value={identity.first_name}
                                        onChange={(v) => setIdentity({ ...identity, first_name: v })}
                                        error={errors.first_name}
                                        testid="diag-input-firstname"
                                    />
                                    <Field
                                        icon={Mail}
                                        label="Email professionnel"
                                        type="email"
                                        placeholder="vous@votre-agence.fr"
                                        value={identity.email}
                                        onChange={(v) => setIdentity({ ...identity, email: v })}
                                        error={errors.email}
                                        testid="diag-input-email"
                                    />
                                    <Field
                                        icon={Building2}
                                        label="Nom de l'agence"
                                        placeholder="Agence Immo Excellence"
                                        value={identity.agency_name}
                                        onChange={(v) => setIdentity({ ...identity, agency_name: v })}
                                        error={errors.agency_name}
                                        testid="diag-input-agency"
                                    />
                                    <Field
                                        icon={Hash}
                                        label="Nombre d'agents commerciaux (optionnel)"
                                        type="number"
                                        placeholder="5"
                                        value={identity.nb_agents}
                                        onChange={(v) => setIdentity({ ...identity, nb_agents: v })}
                                        testid="diag-input-agents"
                                    />

                                    <label className="flex items-start gap-2 text-xs text-white/55 cursor-pointer pt-2" data-testid="diag-consent">
                                        <input
                                            type="checkbox"
                                            checked={consent}
                                            onChange={(e) => setConsent(e.target.checked)}
                                            className="mt-0.5 accent-[#FF914D]"
                                        />
                                        <span>J'accepte de recevoir mon diagnostic et les actualités Homly par email. Conforme RGPD. Désinscription en 1 clic.</span>
                                    </label>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleNextFromIdentity}
                                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold shadow-2xl shadow-[#681793]/30 hover:scale-[1.01] transition-transform"
                                    data-testid="diag-start"
                                >
                                    Commencer le diagnostic
                                    <ArrowRight className="w-4 h-4" />
                                </button>

                                <div className="mt-4 flex items-center justify-center gap-4 text-[11px] text-white/35">
                                    <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> RGPD</span>
                                    <span>·</span>
                                    <span>Aucune CB</span>
                                    <span>·</span>
                                    <span>2 min top chrono</span>
                                </div>
                            </motion.div>
                        ) : currentQuestion ? (
                            /* ─── QUESTION STEPS ─── */
                            <motion.div
                                key={`q-${currentQuestion.id}`}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                transition={{ duration: 0.25 }}
                                data-testid={`diag-step-${currentQuestion.id}`}
                            >
                                <div className="mb-6">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#FF914D]">
                                        Question {step} / {questions.length} · {currentQuestion.kpi_name}
                                    </span>
                                    <h2 className="text-2xl sm:text-3xl font-black text-white mt-3 leading-tight">
                                        {currentQuestion.label}
                                    </h2>
                                </div>

                                <div className="space-y-2.5">
                                    {currentQuestion.options.map((opt) => {
                                        const isSelected = answers[currentQuestion.id] === opt.value;
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => handleAnswer(currentQuestion.id, opt.value)}
                                                className={`w-full text-left px-4 sm:px-5 py-4 rounded-xl border transition-all flex items-center gap-3 group ${
                                                    isSelected
                                                        ? 'bg-gradient-to-r from-[#681793]/30 to-[#FF914D]/20 border-[#FF914D] shadow-lg shadow-[#681793]/20'
                                                        : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-white/20'
                                                }`}
                                                data-testid={`diag-option-${currentQuestion.id}-${opt.value}`}
                                            >
                                                <span className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-all ${
                                                    isSelected ? 'border-[#FF914D] bg-[#FF914D]' : 'border-white/30 group-hover:border-white/50'
                                                }`}>
                                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                                </span>
                                                <span className={`text-sm sm:text-base font-medium ${isSelected ? 'text-white' : 'text-white/75'}`}>
                                                    {opt.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="mt-6 flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white"
                                        data-testid="diag-back"
                                    >
                                        <ArrowLeft className="w-4 h-4" /> Retour
                                    </button>
                                    <div className="text-[11px] text-white/35">
                                        Sélectionnez une réponse pour avancer
                                    </div>
                                </div>
                            </motion.div>
                        ) : null}
                    </AnimatePresence>

                    {errors.submit && (
                        <div className="mt-6 text-sm text-red-400 text-center" data-testid="diag-error">{errors.submit}</div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}

function Field({ icon: Icon, label, type = 'text', placeholder, value, onChange, error, testid }) {
    return (
        <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/50 mb-1.5 flex items-center gap-1.5">
                <Icon className="w-3 h-3" />
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`w-full px-4 py-3 rounded-lg bg-white/[0.04] border ${
                    error ? 'border-red-400/60' : 'border-white/10 focus:border-[#FF914D]/60'
                } text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#FF914D]/20 transition-all`}
                data-testid={testid}
            />
            {error && <span className="text-[11px] text-red-400 mt-1 block">{error}</span>}
        </label>
    );
}

function ResultView({ result, identity, navigate }) {
    const score = result.score;
    const style = LEVEL_STYLES[result.level] || LEVEL_STYLES.developing;
    const circumference = 2 * Math.PI * 80;
    const dashOffset = circumference - (score / 100) * circumference;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full"
            data-testid="diag-result"
        >
            <div className="text-center mb-6">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 mb-4">
                    <Sparkles className="w-3 h-3 text-[#FF914D]" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">Votre rapport personnalisé</span>
                </span>
                <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-1">
                    Voici votre Score Pilotage, {identity.first_name}
                </h1>
                <p className="text-white/50 text-sm">{identity.agency_name}</p>
            </div>

            {/* Score circle */}
            <div className={`relative rounded-3xl bg-gradient-to-br ${style.bg} border ${style.border} backdrop-blur-sm p-8 sm:p-10 text-center mb-6 overflow-hidden`} data-testid="diag-score-card">
                <div className="absolute inset-0 pointer-events-none opacity-50">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-[#681793]/20 rounded-full blur-[80px]" />
                    <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#FF914D]/15 rounded-full blur-[80px]" />
                </div>
                <div className="relative">
                    <svg className="w-44 h-44 mx-auto -rotate-90 mb-4" viewBox="0 0 180 180">
                        <circle cx="90" cy="90" r="80" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
                        <motion.circle
                            cx="90" cy="90" r="80"
                            stroke={style.color}
                            strokeWidth="10"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            initial={{ strokeDashoffset: circumference }}
                            animate={{ strokeDashoffset: dashOffset }}
                            transition={{ duration: 1.4, ease: 'easeOut' }}
                        />
                    </svg>
                    <div className="-mt-32 mb-6">
                        <div className="text-6xl sm:text-7xl font-black text-white leading-none">{score}<span className="text-2xl text-white/40 font-bold">/100</span></div>
                    </div>
                    <div className="mt-12">
                        <p className="text-lg font-bold text-white mb-1">{result.level_label}</p>
                        <p className="text-sm text-white/80 max-w-md mx-auto leading-relaxed">{result.level_msg}</p>
                    </div>
                </div>
            </div>

            {/* Weak KPIs */}
            <div className="mb-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#FF914D] mb-3">Vos 3 angles morts prioritaires</h3>
                <div className="space-y-3">
                    {result.weak_kpis.map((w, i) => {
                        const Icon = ICONS[w.icon] || Target;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 + i * 0.15 }}
                                className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/10"
                                data-testid={`diag-weak-${i}`}
                            >
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
                                    <Icon className="w-5 h-5 text-red-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="text-sm font-bold text-white">{w.name}</h4>
                                        {w.is_weak && (
                                            <span className="text-[9px] font-bold uppercase tracking-wider text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full">Critique</span>
                                        )}
                                    </div>
                                    <p className="text-xs sm:text-sm text-white/65 leading-relaxed"
                                       dangerouslySetInnerHTML={{ __html: w.insight }} />
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Next step CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="rounded-2xl bg-gradient-to-br from-[#681793]/30 via-[#9b4dca]/20 to-[#FF914D]/25 border border-[#FF914D]/40 p-6 sm:p-7 mb-6 text-center"
            >
                <h3 className="text-xl font-black text-white mb-2">Et maintenant ?</h3>
                <p className="text-sm text-white/75 mb-5 leading-relaxed">
                    On parcourt ensemble vos 3 angles morts pendant <strong>15 minutes</strong>, sur un <strong>compte de démonstration</strong> Homly avec des données réalistes. À la fin de l'appel, vous savez exactement combien Homly peut récupérer de CA pour votre agence.
                </p>
                <button
                    type="button"
                    onClick={() => navigate('/agences?demo=true&utm_source=diagnostic')}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold shadow-2xl shadow-[#681793]/30 hover:scale-[1.02] transition-transform"
                    data-testid="diag-cta-demo"
                >
                    Réserver ma démo (15 min)
                    <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-[11px] text-white/45 mt-3">Sans engagement · Sans CB · Réponse sous 2h ouvrées</p>
            </motion.div>

            <p className="text-center text-xs text-white/40">
                <Mail className="w-3 h-3 inline mr-1" /> Un rapport détaillé vient également d'être envoyé à <strong className="text-white/65">{identity.email}</strong>
            </p>
        </motion.div>
    );
}
